/** One event in a calendar grid (time grid, all-day row, month cell): a button coloured by its calendar. */
import type { KeyboardEvent, PointerEvent } from 'react';
import { cn } from '../lib/utils';
import { CALENDAR_COLOR_CLASSES, type CalendarGridEvent } from './model';

export interface EventChipProps {
  event: CalendarGridEvent;
  selected: boolean;
  onSelect: (id: string) => void;
  /** Accessible name and tooltip (title, time, location). */
  label: string;
  /** Second line with the time (timed events in the time grid); undefined = no time line. */
  time?: string | undefined;
  /** One line only (month cells, all-day row). */
  compact?: boolean;
  className?: string;
  /** The user may move it: grab cursor, and with `resizable` top/bottom handles. */
  draggable?: boolean;
  resizable?: boolean;
  /** Pointer pressed on the chip; `edge` when on a resize handle. */
  onGrab?: ((event: PointerEvent<HTMLElement>, edge: 'start' | 'end' | undefined) => void) | undefined;
  onKeyDown?: ((event: KeyboardEvent<HTMLButtonElement>) => void) | undefined;
  /** Shown faded while its moved copy is dragged. */
  faded?: boolean;
  /** `data-context-id` for a surrounding ContextMenuArea. */
  contextId?: string | undefined;
  /** Roving tab stop: only one event of a grid is in the tab order (arrow keys move between them). */
  tabIndex?: number | undefined;
}

export function EventChip({ event, selected, onSelect, label, time, compact, className, draggable, resizable, onGrab, onKeyDown, faded, contextId, tabIndex }: EventChipProps) {
  const colors = CALENDAR_COLOR_CLASSES[event.color];
  const handle = (edge: 'start' | 'end') =>
    resizable && (
      <span
        aria-hidden
        data-edge={edge}
        onPointerDown={(e) => {
          e.stopPropagation();
          onGrab?.(e, edge);
        }}
        className={cn('absolute inset-x-0 z-10 h-1.5 cursor-ns-resize', edge === 'start' ? 'top-0' : 'bottom-0')}
      />
    );
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={selected}
      data-context-id={contextId}
      data-event-chip=""
      tabIndex={tabIndex}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(event.id);
      }}
      onPointerDown={draggable ? (e) => onGrab?.(e, undefined) : undefined}
      onKeyDown={onKeyDown}
      className={cn(
        'relative flex min-w-0 overflow-hidden rounded-sm border-l-4 px-1.5 text-left text-xs leading-tight text-foreground outline-none select-none focus-visible:ring-2 focus-visible:ring-ring',
        compact ? 'items-center gap-1' : 'flex-col py-0.5',
        event.muted ? cn('bg-muted text-muted-foreground', colors.swatch.split(' ')[1]) : event.tentative ? cn('border-dashed', colors.tentative) : colors.chip,
        event.showAs === 'free' && !event.muted && 'bg-background ring-1 ring-border ring-inset',
        event.showAs === 'tentative' && 'bg-hatched',
        event.showAs === 'oof' && 'border-l-cal-purple',
        event.showAs === 'workingElsewhere' && 'border-dotted',
        (event.cancelled || event.declined) && 'line-through opacity-70',
        selected && 'ring-2 ring-ring',
        draggable && 'cursor-grab active:cursor-grabbing',
        faded && 'opacity-40',
        className,
      )}
    >
      {handle('start')}
      {compact && time && <span className="shrink-0 opacity-80">{time}</span>}
      <span className={cn('flex min-w-0 items-center gap-1', event.pending ? 'font-bold' : 'font-medium')}>
        <span className="truncate">{event.title}</span>
        {event.categories && event.categories.length > 0 && (
          <span aria-hidden className="flex shrink-0 gap-0.5">
            {event.categories.slice(0, 3).map((c) => (
              <span key={c.name} className={cn('size-1.5 rounded-full', CALENDAR_COLOR_CLASSES[c.color].swatch)} />
            ))}
          </span>
        )}
        {event.badges}
      </span>
      {!compact && time && <span className="truncate opacity-80">{time}</span>}
      {!compact && event.detail && <span className="truncate opacity-80">{event.detail}</span>}
      {handle('end')}
    </button>
  );
}
